<script>
  import { createEventDispatcher, onDestroy, onMount } from "svelte";

  import Calendar from "@event-calendar/core";
  import ResourceTimeGrid from "@event-calendar/resource-time-grid";
  import Interaction from "@event-calendar/interaction";

  import Select from "svelte-select/Select.svelte";

  import { apiMethods } from "../../../Services/index.svelte";

  import {
    getAccountCalendarEventType,
    getDateFromDateString,
    getDisplayDate,
    getEncounterDischargeDateWithFallBackDuration,
    getOptionLabelForSurgeon,
    getTimeDiffInMillSec,
    getTitle,
    getYYYYMMDD,
    goToURL,
    isNumeric,
    isObjectEmpty,
    openViewEncounterNewPage,
    replaceURL,
    showError,
    showException,
    syncDefaultDoctorFilter,
  } from "../../../Utils/utilFunctions.svelte";

  import {
    NotificationModal,
    SearchSelect,
    SearchInput,
    ResetFilterLink,
    DefaultDoctorFilter,
    DateInput,
  } from "../../../Components/Common/index.svelte";

  import {
    EncounterSidePanelProps,
    isMobile,
    prePopulateAccountOptions,
    user,
  } from "../../../stores";

  import {
    accountCalendarEventType,
    BillingStatus,
  } from "../../../Utils/utilEnums.svelte";

  import {
    DirectCalendarEventTypes,
    editIcon,
    tooltipDisappearTimeDelayMS,
  } from "../../../Utils/utilConstants.svelte";

  import {
    createDoctorEvent,
    customizeEventStyle,
    customizeVerticalViewColWidthAndColor,
    getPatientAge,
    getTimeAfterChanges,
    getTooltipContent,
    getTooltipPosition,
    handleNestedEvents,
    handleSelectedTimeRangeDetails,
    resetCalendarRecurrenceEvent,
    syncMapItem,
    _isAllDayEvent,
  } from "../helpers/calendarHelper.svelte";

  import CalendarEventsNestedEncounterModal from "../components/CalendarEventsNestedEncounterModal.svelte";
  import CalendarDoctorListSidebar from "../components/CalendarDoctorListSidebar.svelte";
  import CalendarRecurrenceModal from "../components/CalendarRecurrenceModal.svelte";
  import ModifyAccountEventModal from "../../Account/components/ModifyAccountEventModal.svelte";
  import DailyCalendarExpandSidePanel from "../components/DailyCalendarExpandSidePanel.svelte";
  import CalendarEventCategorySelector from "../../CalendarCategories/CalendarEventCategorySelector.svelte";
  import CreateSurgeonModal from "../../PatientEncounter/components/CreateSurgeonModal.svelte";
  import {
    CalendarDisplayEncounterTypes,
    CalendarIdleRefreshIntervalSeconds,
    DefaultCalendarTimeGridStartText,
    DefaultColumnWidthPx,
    DefaultDateLocale,
    FullCalendarTitleFormats,
  } from "../helpers/CalendarConstants.svelte";
  import CalendarTooltip from "../components/CalendarTooltip.svelte";
  import { cacheAccountPrePopulateSearchOptions } from "../../../Utils/utilCache.svelte";
  import SurgeonSelector from "../../../Components/Common/SurgeonSelector.svelte";

  import { openViewEncounterSidePanel } from "../../PatientEncounter/helpers/encounterHelper.svelte";

  export let accountId = Number(0);
  export let eventColour = "";
  export let isDoctorCalendar = false;
  export let params = {};
  export let url = "";
  export let isCalendarRoute = false;

  const dispatch = createEventDispatcher();

  $: isDoctorView = accountId > 0 || isDoctorCalendar;

  $: isUserEnableSelectDoctorWhenCreateEvent =
    $user.role == 100 || $user.role == 80 || $user.role == 20;

  $: isLoading = isLoadingEncounters || isLoadingAccountEvents;

  $: _syncSurgeonOptions(accountDoctors, suggestedAccountDoctors);

  $: checkStopIdleRefreshTimer =
    (selectedEncounterProps && !isObjectEmpty(selectedEncounterProps)) ||
    isEventTimeChangeModalOpen ||
    isCreateEventModalOpen ||
    isEventNestedModalOpen ||
    isCalendarRecurrenceModalOpen ||
    isModifyEventModalOpen ||
    isCreateNewSurgeonModalOpen;

  $: if (checkStopIdleRefreshTimer) clearIdleRefreshTimer();
  else startIdleRefreshTimer();

  let ec,
    isLoadingEncounters = false,
    isLoadingAccountEvents = false,
    returnPatientEncounterArr = [],
    displayPatientEncounterArr = [],
    returnAccountEvents = [],
    displayAccountEvents = [],
    doctorsColors = [],
    plugins = [Interaction, ResourceTimeGrid],
    options = {
      view: "resourceTimeGridDay",
      scrollTime: "06:00:00",
      headerToolbar: {
        start: "",
        center: "",
        end: "",
      },
      locale: DefaultDateLocale,
      dayMaxEvents: true,
      slotEventOverlap: false,
      pointer: true,
      allDaySlot: true,
      allDayContent: () => "All Day",
      theme: (theme) => {
        // h-max to make sure sticky parts always on top
        theme.view += " h-max flex-1";
        theme.events += "";
        theme.toolbar += " no-margin";
        theme.header += " sticky top-0 bg-white z-[999]";
        theme.allDay += " all-day-row";
        theme.sidebar += " sticky left-0 bg-white z-[998] border-r";
        return theme;
      },
      eventClick: async (info) => {
        const {
          allDay,
          extendedProps,
          extendedProps: {
            eventType,
            doctorId,
            doctorName,
            doctorNameSc,
            mergedEncounters,
          },
          editable,
          start,
          end,
        } = info.event;

        if (eventType == "encounter") {
          if (editable) {
            // openViewEncounterNewPage(info.event.id);

            // $EncounterSidePanelProps.id = info.event.id;
            openViewEncounterSidePanel({
              encounterId: info.event.id,
              onCloseFunction: () => loadCalendarEvents(false, true),
            });
          } else {
            const filterStartDate = getDateFromDateString(start).split(", ");

            const filterEndDate = getDateFromDateString(end).split(", ");

            selectedEvent = {
              startDate: filterStartDate,
              endDate: filterEndDate,
              doctorId,
              doctorName,
              doctorNameSc,
              eventType,
              nestedEncounter: mergedEncounters ?? [],
            };

            _openEventNestedModal();
          }
        } else _handleClickEvent({ ...extendedProps, allDay });
      },
      eventMouseEnter: (info) => onMouseEnter(info),
      eventMouseLeave: (info) => onMouseLeave(info),
      eventDrop: (e) => eventChangeOnCalendar(e, "drop"),
      eventResize: (e) => eventChangeOnCalendar(e, "resize"),
      datesSet: (info) => _getCalendarTimeRange(info, isCalendarInit),
      viewDidMount: (info) => _getCalendarTimeRange(info, isCalendarInit),
      eventDragStart: (e) => {
        clearIdleRefreshTimer();
        setTimeout(() => {
          customizeEventStyle();
        }, 0);
      },
      eventDragStop: (e) => {
        setTimeout(() => {
          customizeEventStyle();
          !checkStopIdleRefreshTimer && startIdleRefreshTimer();
        }, 0);
      },
      eventResizeStart: (e) => {
        clearIdleRefreshTimer();
        setTimeout(() => {
          customizeEventStyle();
        }, 0);
      },
      eventResizeStop: (e) => {
        setTimeout(() => {
          customizeEventStyle();
          !checkStopIdleRefreshTimer && startIdleRefreshTimer();
        }, 0);
      },
      selectable: true,
      select: (e) => openCreateEventModal(e),
      // hide existing events (with id)
      eventClassNames: (info) => {
        if (isNumeric(info?.event?.id)) return "invisible";
        else return "z10";
      },
    };

  let allDoctors = [],
    searchAccountText,
    searchDoctorArr = [];

  let selectedEventOldDetails = {},
    selectedEventNewDetails = {},
    isEventTimeChangeModalOpen = false,
    isUpdatingEventTime = false;

  let isSearchingSurgeon = false,
    searchAccountDoctorsArr = [],
    selectedAccountDoctors = [];

  let calendarStartDate, calendarEndDate;

  let isCalendarInit = false;

  let isCreateEventModalOpen = false,
    selectedTimeRangeDetails = {},
    createEventProps = {},
    isCreatingEvent = false;

  let isLoadingAccount = false,
    selectedDoctor = {};

  let isEventNestedModalOpen = false,
    isModifyEventModalOpen = false,
    eventNewValues = [],
    selectedEvent = {};

  let isVisibleDoctorsCollapsed = false,
    isLoadingDoctors = false;

  let filters = { jumpDate: new Date() };

  let isCalendarRecurrenceModalOpen = false;

  let savedDoctorId;

  let selectedEncounterProps = {};

  let selectedHospital,
    skipEncounterLoading,
    skipEventsLoading,
    selectedSurgeon,
    surgeonOptions = [],
    hospitalOptions = [],
    suggestedAccountDoctors = [],
    suggestedHospitals = [],
    accountDoctors = [],
    selectedDoctorID,
    isCreateNewSurgeonModalOpen = false,
    isLoadingSuggestedAccountDoctors = false,
    isLoadingSuggestedHospitals = false;

  let displayDateRange = "",
    showMonthlyTooltip = false,
    tooltipInfo = {};

  let isFetchedHospital = false,
    isMonthView = false,
    tooltipDisplayDelayTimer,
    isFetchedSurgeon = false;

  let idleRefreshTimer;

  onMount(async () => {
    clearIdleRefreshTimer();

    filters.billingStatus = "0,1,2,3,4";

    if (params.query) await updateSearchParamsByQuery(params.query);

    filters = await syncDefaultDoctorFilter(filters, "doctorId");

    if (filters.doctorId)
      searchDoctorArr = filters.doctorId.split(",").map((id) => Number(id));

    if (accountId || searchDoctorArr.length) await loadCalendarEvents(true);
    else await drawCalendar(true);

    isCalendarInit = true;

    if (accountId) selectedDoctorID = accountId;
  });

  onDestroy(() => {
    clearIdleRefreshTimer();
  });

  const initScrollToDayStart = () => {
    if (DefaultCalendarTimeGridStartText && ec) {
      const allTimeSlotsEle = document.getElementsByClassName("ec-time");
      if (allTimeSlotsEle) {
        for (let timeEle of allTimeSlotsEle) {
          if (
            timeEle
              .getAttribute("datetime")
              ?.includes(DefaultCalendarTimeGridStartText)
          )
            timeEle.scrollIntoView({
              behavior: "instant",
              block: "start",
            });
        }
      }
    }
  };

  const updateSearchParamsByQuery = async (query) => {
    const arr = query.split("&");

    if (!arr.length) return;

    for (let i = 0; i < arr.length; i++) {
      if (arr[i].includes("viewType=")) {
        filters.viewType = arr[i].split("=")[1];
        ec.setOption("view", filters.viewType);
        if (FullCalendarTitleFormats[filters.viewType])
          ec.setOption(
            "titleFormat",
            FullCalendarTitleFormats[filters.viewType],
          );
      }

      if (arr[i].includes("billingStatus="))
        filters.billingStatus = isNaN(Number(arr[i].split("=")[1]))
          ? "0,1,2,3,4,5"
          : Number(arr[i].split("=")[1]);

      if (arr[i].includes("doctorId="))
        searchDoctorArr = arr[i]
          .split("=")[1]
          .split(",")
          .map((doctorId) => Number(doctorId));

      if (arr[i].includes("surgeonIDs="))
        selectedAccountDoctors = arr[i].split("=")[1].split(",");

      if (arr[i].includes("jumpDate=")) {
        filters.jumpDate = arr[i].split("=")[1];
        ec.setOption("date", new Date(filters.jumpDate));
      }

      if (arr[i].includes("eventType=")) {
        filters.eventType = arr[i].split("=")[1];
      }

      if (arr[i].includes("eventCategory=")) {
        filters.eventCategory = arr[i].split("=")[1];
      }
    }
  };

  const eventChangeOnCalendar = async (e, type = "") => {
    const {
      oldEvent,
      event,
      event: {
        allDay,
        extendedProps,
        extendedProps: { eventType, mergedEncounters },
      },
    } = e;

    selectedEventOldDetails = {
      ...oldEvent,
      oldEventType:
        typeof oldEvent.extendedProps.eventType == "string"
          ? oldEvent.extendedProps.eventType
          : getAccountCalendarEventType(oldEvent.extendedProps.eventType),
    };

    selectedEventNewDetails = { ...event };

    // merged encounters resize is not supported
    if (
      type == "resize" &&
      eventType == "encounter" &&
      mergedEncounters?.length
    ) {
      discardEventTimeChange();
      return;
    }

    if (mergedEncounters?.length) {
      const startTime = selectedEventOldDetails.start;
      const endTime = selectedEventNewDetails.start;

      const timeChanges = getTimeDiffInMillSec(endTime, startTime);

      selectedEventNewDetails.mergedEncounters = mergedEncounters.map(
        (encounter) => ({
          ...encounter,
          scheduledDate: getTimeAfterChanges(
            encounter.scheduledDate,
            timeChanges,
          ),
          dischargeDate: getTimeAfterChanges(
            getEncounterDischargeDateWithFallBackDuration(encounter),
            timeChanges,
          ),
        }),
      );
    }

    if (eventType == "encounter") openEventTimeChangeModal();
    else {
      const [fromDate, fromTime] = getDateFromDateString(event.start).split(
        ", ",
      );
      const [toDate, toTime] = getDateFromDateString(event.end).split(", ");

      const targetEvent = returnAccountEvents.find(
        (evt) => evt.id == extendedProps.eventId && evt.eventType == eventType,
      );

      selectedEvent = {
        ...targetEvent,
        ...extendedProps,
        id: extendedProps.eventId,
        fromDate: fromDate.split("/").reverse().join("-"),
        fromTime,
        toDate: toDate.split("/").reverse().join("-"),
        toTime,
        accountId: extendedProps.doctorId,
        isFullDay: allDay,
        isRepeating:
          (targetEvent.eventType == 1 || targetEvent.eventType == 2) &&
          targetEvent.repeatIntervalType > 0,
      };

      eventNewValues = {
        fromDate: fromDate.split("/").reverse().join("-"),
        fromTime,
        toDate: toDate.split("/").reverse().join("-"),
        toTime,
      };

      isModifyEventModalOpen = true;
    }
  };

  const loadCalendarEvents = async (
    scrollToDayStart = false,
    isReload = false,
  ) => {
    if (isLoadingDoctors) return;

    if (!isReload) options.events = [];

    skipEncounterLoading = false;
    skipEventsLoading = false;

    switch (filters.eventType) {
      case "encounter":
      case DirectCalendarEventTypes.PatientEncounter:
        skipEventsLoading = true;
        break;
      case DirectCalendarEventTypes.Leave:
        skipEncounterLoading = true;
        break;
      case DirectCalendarEventTypes.List:
      case DirectCalendarEventTypes.OnCall:
      default:
        break;
    }

    if (!accountId && !searchDoctorArr.length) {
      returnAccountEvents = [];
      returnPatientEncounterArr = [];
    } else {
      await loadAccountEvents(isReload);
      await loadPatientEncounters(isReload);
    }

    await drawCalendar(scrollToDayStart);

    // reload nested encounter modal details
    if (isEventNestedModalOpen && selectedEvent?.id) {
      handleEditEventDetails(selectedEvent);
      isModifyEventModalOpen = false;
    }

    startIdleRefreshTimer();
  };

  const loadPatientEncounters = async (isReload = false) => {
    if (skipEncounterLoading) {
      returnPatientEncounterArr = [];
      displayPatientEncounterArr = [];
      return;
    }

    if (!isReload) isLoadingEncounters = true;

    await apiMethods.getMethod
      .getPatientEncounter(0, 999999, _getSearchParams())
      .then((res) => _handlePatientEncounters(res));

    isLoadingEncounters = false;
  };

  const loadAccountEvents = async (isReload = false) => {
    if (skipEventsLoading) {
      returnAccountEvents = [];
      displayAccountEvents = [];
      return;
    }

    if (!isReload) isLoadingAccountEvents = true;

    const searchParams = _getSearchParams();

    const eventFilters = {
      "filter[fromDateFrom]": getDateFromDateString(calendarStartDate)
        .split(", ")[0]
        .split("/")
        .reverse()
        .join("-"),
      "filter[toDateTo]": getDateFromDateString(calendarEndDate)
        .split(", ")[0]
        .split("/")
        .reverse()
        .join("-"),
    };

    if (searchParams["filter[doctorId]"])
      eventFilters["filter[doctorId]"] = searchParams["filter[doctorId]"];

    if (filters.eventType || filters.eventType === 0)
      eventFilters["filter[eventtype]"] = filters.eventType;

    if (filters.eventCategory)
      eventFilters["filter[eventCategory]"] = filters.eventCategory;

    if (selectedAccountDoctors.length)
      eventFilters["filter[surgeonUniqueID]"] =
        selectedAccountDoctors.join(",");

    await apiMethods.getMethod
      .getAccountCalendarEvents(0, 999999, eventFilters)
      .then((res) => _handleAccountEvents(res))
      .catch((err) => showException(err));

    isLoadingAccountEvents = false;
  };

  const buildDoctorResources = (docList) => {
    if (!docList || !docList.length) return [];
    else
      return docList.map((doc) => ({
        id: doc.id,
        title: `${doc.titleName} ${doc.fullName}`,
      }));
  };

  const drawCalendar = async (scrollToDayStart = false) => {
    options.events = await createEvents();
    options.resources = buildDoctorResources(searchDoctorArr);

    setTimeout(() => {
      customizeEventStyle();

      customizeVerticalViewColWidthAndColor(searchDoctorArr);

      if (scrollToDayStart) initScrollToDayStart();
    }, 0);
  };

  const createEvents = async () => {
    const { events, restEncounters } = await handleNestedEvents(
      returnAccountEvents,
      returnPatientEncounterArr,
    );

    displayAccountEvents = [...events];
    displayPatientEncounterArr = [...restEncounters];

    const encounterEvents = await _createEncounterEvents();
    const doctorEvents = await _createDoctorEvents();

    return filters?.showEncountersOnDailyPlanner
      ? [...encounterEvents, ...doctorEvents]
      : doctorEvents;
  };

  const handleSearchDoctorsChange = async (searchResult) => {
    searchDoctorArr = [...searchResult.sort((a, b) => a - b)];

    updateURL();

    await loadCalendarEvents();
  };

  const openEventTimeChangeModal = () => (isEventTimeChangeModalOpen = true);

  const closeEventTimeChangeModal = (isDiscard = false) => {
    if (isDiscard) discardEventTimeChange();

    isEventTimeChangeModalOpen = false;
    selectedEventNewDetails = {};
    selectedEventOldDetails = {};
    selectedEncounterProps = {};
    selectedEvent = {};
  };

  const updateEventTime = async () => {
    isUpdatingEventTime = true;

    await _updateEncounterTime();

    isUpdatingEventTime = false;

    closeEventTimeChangeModal();

    displayAccountEvents = [...returnAccountEvents];
    displayPatientEncounterArr = [...returnPatientEncounterArr];

    drawCalendar();
  };

  const discardEventTimeChange = () => {
    displayPatientEncounterArr.map((encounter) => {
      if (encounter.id == selectedEventOldDetails.id)
        return {
          ...encounter,
          scheduledDate: getDateFromDateString(selectedEventOldDetails.start),
          dischargeDate: getDateFromDateString(selectedEventOldDetails.end),
        };
      return encounter;
    });

    drawCalendar();
  };

  const handleFilterSelect = () => {
    updateURL();

    if (accountId || searchDoctorArr.length) loadCalendarEvents();
  };

  const handleSelectJumpDate = () => {
    updateURL();
    ec.setOption("date", new Date(filters.jumpDate));
  };

  const updateURL = () => {
    if (isCalendarRoute) replaceURL(`${url}${_getParamsString()}`);
  };

  const handleSelectSurgeonFromSearch = (searchResult) => {
    selectedAccountDoctors = [...searchResult];

    handleFilterSelect();
  };

  const openCreateEventModal = (e) => {
    selectedDoctor = {
      id: e?.resource?.id,
      key: e?.resource?.id,
      name: e?.resource?.title,
      value: e?.resource?.title,
    };
    selectedTimeRangeDetails = handleSelectedTimeRangeDetails(e);

    createEventProps = {
      ...createEventProps,
      fromDate: selectedTimeRangeDetails.startStr.split("T")[0],
      fromTime: selectedTimeRangeDetails.startStr.split("T")[1].slice(0, 5),
      toDate: selectedTimeRangeDetails.endStr.split("T")[0],
      toTime: selectedTimeRangeDetails.endStr.split("T")[1].slice(0, 5),
      eventType: "list",
    };

    isCreateEventModalOpen = true;

    // to remove the selection color (default to blue but maybe different)
    ec.unselect();
  };

  const closeCreateEventModal = () => {
    isCreateEventModalOpen = false;
    selectedTimeRangeDetails = {};
    createEventProps = {};
    clearSelectedAccount();
    accountDoctors = [];
    selectedHospital = undefined;
    selectedSurgeon = undefined;

    isFetchedHospital = false;
    isFetchedSurgeon = false;
  };

  const loadAccounts = async (filterText) => {
    await cacheAccountPrePopulateSearchOptions();

    if (!filterText)
      return $prePopulateAccountOptions.length
        ? $prePopulateAccountOptions
        : [];

    return $prePopulateAccountOptions.length
      ? $prePopulateAccountOptions.filter(({ value }) =>
          value.toLowerCase().includes(filterText.toLowerCase()),
        )
      : [];
  };

  const clearSelectedAccount = () => {
    selectedDoctor = null;
    selectedHospital = null;
    selectedSurgeon = null;
    hospitalOptions = [];
    accountDoctors = [];
    suggestedHospitals = [];
    suggestedAccountDoctors = [];
  };

  const handleToggleRecurrenceModalOpen = (eventObj = {}) => {
    if (!eventObj.isRepeating) return;

    isCalendarRecurrenceModalOpen = true;
  };

  const confirmCreateEvent = async () => {
    if (createEventProps.eventType === "global") {
      createEventProps.doctorId = undefined;
    } else {
      // if user is doctor, assign doctor id directly
      if (!isUserEnableSelectDoctorWhenCreateEvent) {
        if (savedDoctorId) createEventProps.doctorId = Number(savedDoctorId);
        else
          await apiMethods.getMethod
            .getAccountByUserId($user.id)
            .then((res) => {
              savedDoctorId = createEventProps.doctorId = res.id;
            });
      } else if (isDoctorView) createEventProps.doctorId = accountId;
      else if (selectedDoctor?.id)
        createEventProps.doctorId = selectedDoctor.id;
      else return showMissingDoctorError();
    }

    if (!searchDoctorArr.includes(createEventProps.doctorId))
      searchDoctorArr.push(createEventProps.doctorId);

    if (createEventProps.eventType == "encounter") {
      updateURL();

      selectedEncounterProps = {
        startDate: createEventProps.fromDate,
        endDate: createEventProps.endDate,
        startTime: createEventProps.fromTime,
        endTime: createEventProps.endTime,
        doctorID: createEventProps.doctorId,
        afterSaveEncounter: () => closeExpandSidePanel(true),
      };
    } else {
      const eventTypeMap = {
        leave: Number(0),
        list: Number(1),
        onCall: Number(2),
        global: Number(5),
      };

      let eventObj = {
        ...createEventProps,
        doctorId: createEventProps.doctorId,
        eventType: eventTypeMap[createEventProps.eventType],
        fromDate: `${createEventProps.fromDate}T${createEventProps.fromTime}:00`,
        toDate: `${createEventProps.toDate}T${createEventProps.toTime}:00`,
      };

      if (
        eventObj.eventType == 0 ||
        (eventObj.hasOwnProperty("isRepeating") && !eventObj.isRepeating)
      )
        eventObj = resetCalendarRecurrenceEvent(eventObj);

      if (eventObj.eventType === 1) {
        if (selectedHospital?.id) eventObj.hospitalId = selectedHospital.id;

        if (selectedSurgeon?.id) eventObj.surgeonId = selectedSurgeon.id;
      }

      isCreatingEvent = true;

      await apiMethods.postMethod
        .createAccountCalendarEvent(eventObj)
        .then(async (res) => {
          if (!res || res.error)
            showError(
              res.error ??
                "Failed to create event, please refresh the page and try again and contact support if the issue persists.",
            );
          else {
            updateURL();
            await loadCalendarEvents();
          }
        })
        .catch((err) => showException(err));

      isCreatingEvent = false;
    }

    closeCreateEventModal();
  };

  let doctorSelectorWrapperStyle = "";

  const showMissingDoctorError = () => {
    doctorSelectorWrapperStyle = "border: 2px solid red; border-radius: 5px;";

    setTimeout(() => {
      doctorSelectorWrapperStyle = "";
    }, 3000);
  };

  const closeExpandSidePanel = async (requireReloadEncounter = false) => {
    if (requireReloadEncounter) {
      if (accountId || searchDoctorArr.length)
        await handleSearchDoctorsChange(
          accountId ? [accountId] : searchDoctorArr,
        );
    }

    closeEventTimeChangeModal(!requireReloadEncounter);
  };

  const handleEditNestedEncounter = (encounter = {}, isDuplicate = false) => {
    if (isObjectEmpty(encounter)) return;

    selectedEncounterProps = { ...encounter, isDuplicate };
    closeNestedEncounterModal();
  };

  const handleEditEventDetails = (event = {}) => {
    if (isObjectEmpty(event)) return;

    const targetEvent = returnAccountEvents.find(
      (evt) => evt.id == event.id && evt.eventType == event.eventType,
    );

    selectedEvent = {
      // make sure targetevent is after event!!!
      ...event,
      ...targetEvent,
      isRepeating:
        (targetEvent.eventType == 1 || targetEvent.eventType == 2) &&
        targetEvent.repeatIntervalType > 0,
      isFullDay: event.allDay,
    };

    eventNewValues = {};

    isModifyEventModalOpen = true;
  };

  // HELPER FUNCTIONS
  const _handlePatientEncounters = (res) => {
    if (res.error) showError(res.error);
    else {
      returnPatientEncounterArr = [...res.results];
      displayPatientEncounterArr = [...res.results];
    }
  };

  const _handleAccountEvents = (res) => {
    if (res.error) showError(res.error);
    else {
      returnAccountEvents = [...res.results];
      displayAccountEvents = [...res.results];
    }
  };

  const _createEncounterEvents = async () => {
    const calendarType = ec?.getView().type;
    const typeArrNoHandle = ["listWeek", "timeGridDay"];

    if (!displayPatientEncounterArr.length) return [];

    if (typeArrNoHandle.includes(calendarType))
      return await _createDefaultEncounterEvents();
    else if (
      calendarType == "timeGridWeek" ||
      calendarType == "resourceTimeGridDay"
    )
      return await _createMergedEncounterEvents();
    else if (calendarType == "dayGridMonth")
      return await _createMergedEncounterEvents(true);

    return [];
  };

  const _createDefaultEncounterEvents = async () =>
    await _syncEncounterEventsArr(displayPatientEncounterArr);

  const _createMergedEncounterEvents = async (isMonthEvents = false) => {
    const encounterMap = {};

    const res = [];

    displayPatientEncounterArr.forEach((encounter) => {
      const { doctorId, hospitalId, scheduledDate, surgeonId } = encounter;

      const dateKey = scheduledDate.split("T")[0];

      if (!encounterMap[doctorId]) encounterMap[doctorId] = {};

      if (!encounterMap[doctorId][dateKey])
        encounterMap[doctorId][dateKey] = {};

      if (!hospitalId) {
        res.push({
          ...encounter,
          editable: true,
          eventType: "encounter",
        });
        return;
      }

      if (!encounterMap[doctorId][dateKey][hospitalId])
        encounterMap[doctorId][dateKey][hospitalId] = {};

      if (!encounterMap[doctorId][dateKey][hospitalId][surgeonId])
        encounterMap[doctorId][dateKey][hospitalId][surgeonId] = [];

      encounterMap[doctorId][dateKey][hospitalId][surgeonId].push(encounter);
    });

    let targetEncounters = [];

    for (const doctorId in encounterMap) {
      for (const dateKey in encounterMap[doctorId]) {
        for (const hospitalId in encounterMap[doctorId][dateKey]) {
          targetEncounters = [];
          if (isMonthEvents) {
            for (const surgeonKey in encounterMap[doctorId][dateKey][
              hospitalId
            ]) {
              targetEncounters = targetEncounters.concat(
                encounterMap[doctorId][dateKey][hospitalId][surgeonKey],
              );
            }

            if (targetEncounters.length > 1) {
              const mergeDate = _mergeDates(targetEncounters);
              res.push({
                ...targetEncounters[0],
                ...mergeDate,
                editable: false,
                eventType: "encounter",
                timeArr: _getMergeTimeArr(targetEncounters),
                isMergedEncounter: true,
                mergedEncounters: targetEncounters,
              });
            } else
              res.push({
                ...targetEncounters[0],
                editable: true,
                eventType: "encounter",
                isMergedEncounter: false,
              });
          } else
            for (const surgeonKey in encounterMap[doctorId][dateKey][
              hospitalId
            ]) {
              targetEncounters =
                encounterMap[doctorId][dateKey][hospitalId][surgeonKey];

              if (targetEncounters.length > 1) {
                const mergeDate = _mergeDates(targetEncounters);
                res.push({
                  ...targetEncounters[0],
                  ...mergeDate,
                  editable: false,
                  eventType: "encounter",
                  isMergedEncounter: true,
                  mergedEncounters: targetEncounters,
                });
              } else
                res.push({
                  ...targetEncounters[0],
                  editable: true,
                  eventType: "encounter",
                  isMergedEncounter: false,
                });
            }
        }
      }
    }

    return await _syncEncounterEventsArr(res, isMonthEvents);
  };

  const _mergeDates = (encounters) => {
    let minScheduledDate = new Date(encounters[0].scheduledDate);
    let maxDischargeDate = new Date(encounters[0].dischargeDate);

    for (let i = 1; i < encounters.length; i++) {
      const encounter = encounters[i];
      const scheduledDate = new Date(encounter.scheduledDate);
      const dischargeDate = new Date(encounter.dischargeDate);
      if (scheduledDate < minScheduledDate) {
        minScheduledDate = scheduledDate;
      }
      if (dischargeDate > maxDischargeDate) {
        maxDischargeDate = dischargeDate;
      }
    }
    return {
      scheduledDate: minScheduledDate,
      dischargeDate: maxDischargeDate,
    };
  };

  const _getMergeTimeArr = (encounters) => {
    const res = [];

    encounters.forEach((encounter) => {
      const {
        scheduledDate,
        dischargeDate,
        hospital,
        surgeon,
        patient,
        procedureName,
      } = encounter;

      res.push({
        start: scheduledDate,
        end: dischargeDate,
        hospital: hospital ? hospital.name : "",
        surgeon: surgeon
          ? `${getTitle(surgeon.title)} ${surgeon.firstName} ${
              surgeon.lastName
            }`
          : "",
        patientName: `${patient.firstName} ${patient.lastName}`,
        patientGender: patient.sex,
        patientAge: getPatientAge(patient.birthDate),
        patientProcedure: procedureName,
      });
    });

    return res;
  };

  const _syncEncounterEventsArr = async (arr = [], isMonthEvents = false) => {
    const result = [];

    await arr.map(async (item, index) => {
      const syncedItem = await syncMapItem(
        item,
        index,
        isMonthEvents,
        _getTooltipContent,
        isDoctorView,
        doctorsColors,
        eventColour,
      );

      result.push(syncedItem);
    });

    return result;
  };

  const _createDoctorEvents = async () => {
    const res = [];

    if (!displayAccountEvents.length) return res;

    const viewType = ec.getView().type;

    await displayAccountEvents.map((event) => {
      res.push(createDoctorEvent(event, viewType, isDoctorCalendar, calendarStartDate, calendarEndDate));
    });

    return res;
  };

  const _getTooltipContent = (
    item,
    startDate,
    endDate,
    showAdditionalInfo = true,
    isMonthEvents = false,
    index,
  ) => {
    const tooltipPosition = _getTooltipPosition(startDate, endDate, index);

    const viewType = ec.getView().type;

    return getTooltipContent(
      item,
      startDate,
      endDate,
      showAdditionalInfo,
      isMonthEvents,
      tooltipPosition,
      isDoctorCalendar,
      viewType,
    );
  };

  const _getTooltipPosition = (start, end, index) => {
    const viewType = ec.getView().type;
    const view = ec.getView();

    return getTooltipPosition(start, end, viewType, view, index);
  };

  const _getCalendarTimeRange = (info = {}, loadData = false) => {
    displayDateRange = getDisplayDateRange(info);

    const isFromDatesSet = !info.hasOwnProperty("type");

    calendarStartDate = isFromDatesSet
      ? new Date(info.view.currentStart)
      : new Date(info.currentStart);
    calendarEndDate = isFromDatesSet
      ? new Date(info.view.currentEnd)
      : new Date(info.currentEnd);

    if (!loadData) return;

    if (!isFromDatesSet) {
      filters.viewType = info.type;
      updateURL();
    }

    if (accountId || searchDoctorArr.length) loadCalendarEvents(true);
  };

  const getDisplayDateRange = (info) => {
    if (info.title) {
      return info.title;
    } else if (info.view) {
      return info.view?.title;
    }

    return "";
  };

  const _getParamsString = () => {
    let str = "";
    const params = _getSearchParams();

    if (!params || isObjectEmpty(params)) return str;

    if (!accountId) str += "/";

    if (params["viewType"]) str += `&viewType=${params["viewType"]}`;

    if (
      (params["filter[billingStatus]"] ||
        params["filter[billingStatus]"] == Number(0)) &&
      params["filter[billingStatus]"] != "0,1,2,3,4"
    )
      str += `&billingStatus=${params["filter[billingStatus]"]}`;

    if (params["filter[doctorId]"] && !accountId)
      str += `&doctorId=${params["filter[doctorId]"]}`;

    if (params["filter[surgeonUniqueID]"])
      str += `&surgeonIDs=${params["filter[surgeonUniqueID]"]}`;

    if (params["filter[jumpDate]"])
      str += `&jumpDate=${params["filter[jumpDate]"]}`;

    return str;
  };

  const _getSearchParams = () => {
    const filterStartDate = getDateFromDateString(calendarStartDate)
      .split(", ")[0]
      .split("/")
      .reverse()
      .join("-");
    const filterEndDate = getDateFromDateString(calendarEndDate)
      .split(", ")[0]
      .split("/")
      .reverse()
      .join("-");

    let params = {
      sort: "scheduleddate",
      "filter[simplified]": 1,
      "filter[status]": CalendarDisplayEncounterTypes.join(),
      "filter[scheduledDateFrom]": filters.dateFrom ?? filterStartDate,
      "filter[scheduledDateTo]": filters.dateTo ?? filterEndDate,
    };

    if (filters.viewType) params["viewType"] = filters.viewType;

    if (accountId) params["filter[doctorId]"] = Number(accountId);
    else if (
      searchDoctorArr.length &&
      searchDoctorArr.length !== allDoctors.length
    ) {
      const doctorIds = searchDoctorArr.map((a) => {
        if (typeof a == "object") return a.id;
        return a;
      });
      params["filter[doctorId]"] = doctorIds.join();
    }

    if (filters.billingStatus || filters.billingStatus == 0)
      params["filter[billingStatus]"] = filters.billingStatus;

    if (selectedAccountDoctors.length)
      params["filter[surgeonUniqueID]"] = selectedAccountDoctors.join(",");

    if (filters.jumpDate) params["filter[jumpDate]"] = filters.jumpDate;

    return params;
  };

  const _updateEncounterTime = async () => {
    let selectedEncounters;

    if (!selectedEventNewDetails.mergedEncounters) {
      selectedEncounters = [
        displayPatientEncounterArr.find(
          (encounter) => encounter.id == selectedEventNewDetails.id,
        ),
      ];
      selectedEncounters = selectedEncounters.map((encounter) => {
        const newStartDateArr = getDateFromDateString(
          selectedEventNewDetails.start,
        ).split(", ");
        const newEndDateArr = getDateFromDateString(
          selectedEventNewDetails.end,
        ).split(", ");

        return {
          ...encounter,
          scheduledDate:
            newStartDateArr[0].split("/").reverse().join("-") +
            "T" +
            newStartDateArr[1],
          dischargeDate:
            newEndDateArr[0].split("/").reverse().join("-") +
            "T" +
            newEndDateArr[1],
        };
      });
    } else selectedEncounters = selectedEventNewDetails.mergedEncounters;

    let backupEncounters = [...returnPatientEncounterArr];

    const batchSize = 3;

    try {
      for (let i = 0; i < selectedEncounters.length; i += batchSize) {
        const batch = selectedEncounters.slice(i, i + batchSize);

        await Promise.all(
          batch.map(async (encounter) => {
            const res = await apiMethods.putMethod.updateEncounterScheduleDate(
              encounter.id,
              {
                scheduledDate: encounter.scheduledDate,
                dischargeDate: encounter.dischargeDate,
              },
            );

            if (!res || res.error || !res.id) {
              const err =
                res.error ??
                "Failed to update encounter date/time, please refresh the page and try again and contact support if the issue persists.";
              showError(err);
              throw new Error(err);
            } else {
              returnPatientEncounterArr = returnPatientEncounterArr.map(
                (encounter) => {
                  if (encounter.id == res.id) return res;
                  return encounter;
                },
              );
            }
          }),
        );
      }
    } catch (err) {
      showError(
        "Failed to update some encounters, please refresh the page and try again and contact support if the issue persists.",
      );
      discardEventTimeChange();
      returnPatientEncounterArr = [...backupEncounters];
    }
  };

  const _handleClickEvent = (event = {}) => {
    if (!event || isObjectEmpty(event)) return;

    const { allDay, eventType } = event;

    selectedEvent = { ...event };

    if (eventType) _openEventNestedModal();
    else _openModifyLeaveEventModal(allDay);
  };

  const _openEventNestedModal = () => (isEventNestedModalOpen = true);

  const closeNestedEncounterModal = () => {
    selectedEvent = {};
    isEventNestedModalOpen = false;
  };

  const closeEditEventModal = () => {
    discardEventTimeChange();
    isModifyEventModalOpen = false;
    if (!isEventNestedModalOpen) selectedEvent = {};
    else selectedEvent = { ...selectedEvent };
  };

  const _openModifyLeaveEventModal = (allDay = false) => {
    selectedEvent = returnAccountEvents.find(
      (event) => event.eventType == 0 && event.id == selectedEvent.eventId,
    );

    selectedEvent = {
      ...selectedEvent,
      fromTime: selectedEvent.fromDate.split("T")[1].slice(0, 5),
      toTime: selectedEvent.toDate.split("T")[1].slice(0, 5),
      accountId: selectedEvent.doctorId,
      isFullDay: allDay,
    };

    eventNewValues = {};

    isModifyEventModalOpen = true;
  };

  const loadHospitals = async (searchText) => {
    const res = searchText
      ? await apiMethods.getMethod.getHospitals(0, 999999, {
          search: searchText,
        })
      : [];

    return _handleSearchHospitals(res.results?.length > 0 ? res.results : []);
  };

  const _handleSearchHospitals = (res = []) => {
    hospitalOptions = [];

    if (!res.length && !suggestedHospitals.length) return [];

    const suggestedOptions = [];

    if (res.length) {
      hospitalOptions = [...res];
      return [...res];
    }

    if (!suggestedHospitals.length) return [];

    suggestedHospitals.map((hospital) => {
      suggestedOptions.push({
        ...hospital,
        groupName: "Suggested Hospitals",
      });
    });

    hospitalOptions = [...suggestedOptions];

    return [...suggestedOptions];
  };

  const groupBy = (option) => option.groupName;

  const loadSuggestedAccountDoctor = async () => {
    if (isFetchedSurgeon) return;

    const doctorID = isDoctorView ? accountId : selectedDoctor?.id;

    if (!doctorID) return;

    isLoadingSuggestedAccountDoctors = true;

    await apiMethods.getMethod.getSuggestedSurgeon(doctorID).then((res) => {
      if (res.results) suggestedAccountDoctors = [...res.results];
    });

    //filter surgeons and take only once by unique id
    suggestedAccountDoctors = [
        ...new Map(suggestedAccountDoctors.map(doc => [doc.providerUniqueId, doc])).values()
    ];

    if (suggestedAccountDoctors.length > 5)
      suggestedAccountDoctors = suggestedAccountDoctors.slice(0, 5);

    await loadAccountDoctors(doctorID);

    isFetchedSurgeon = true;

    isLoadingSuggestedAccountDoctors = false;
  };

  const loadSuggestedHospitals = async () => {
    if (isFetchedHospital) return;

    const doctorID = isDoctorView ? accountId : selectedDoctor?.id;

    if (!doctorID) return;

    isLoadingSuggestedHospitals = true;

    await apiMethods.getMethod.getSuggestedHospital(doctorID).then((res) => {
      if (res.results) {
        suggestedHospitals = [...res.results];
        _handleSearchHospitals([]);
      }
    });

    isFetchedHospital = true;

    isLoadingSuggestedHospitals = false;
  };

  const loadAccountDoctors = async (id) => {
    await apiMethods.getMethod
      .getAccountDoctors(0, 999999, {
        "filter[doctorId]": id,
      })
      .then((res) => {
        if (res.results) accountDoctors = [...res.results];
        else accountDoctors = [];
      });
  };

  const _syncSurgeonOptions = (accountDoctors = [], suggestedSurgeons = []) => {
    surgeonOptions = [];

    let uniqueAccountDoctors = [
      ...new Map(accountDoctors.map(doc => [doc.providerUniqueId, doc])).values()
    ];

    if (!suggestedSurgeons)
      uniqueAccountDoctors.map((doctor) => {
        surgeonOptions.push({
          ...doctor,
          displayLabel: getOptionLabelForSurgeon(doctor),
        });
      });
    else {
      const suggestedSurgeonUniqueIds = [];
      const len = Math.min(suggestedSurgeons.length, 5);

      for (let i = 0; i < len; i++) {
        const surgeon = suggestedSurgeons[i];

        surgeonOptions.push({
          ...surgeon,
          groupName: "Suggested surgeons",
          displayLabel: getOptionLabelForSurgeon(surgeon),
        });

        suggestedSurgeonUniqueIds.push(surgeon.providerUniqueId);
      }

      uniqueAccountDoctors.map((surgeon) => {
        if (
          !suggestedSurgeonUniqueIds.includes(
            surgeon.providerUniqueId.toString(),
          )
        )
          surgeonOptions.push({
            ...surgeon,
            groupName: "Other Surgeons",
            displayLabel: getOptionLabelForSurgeon(surgeon),
          });
      });
    }
  };

  const openCreateNewSurgeonModal = () => {
    isCreateNewSurgeonModalOpen = true;
  };

  const closeCreateNewSurgeonModal = () =>
    (isCreateNewSurgeonModalOpen = false);

  let isReloadingEvent = false;

  const reloadReOpenCurrentEventModal = async (event) => {
    isReloadingEvent = true;
    await loadCalendarEvents();

    if (event?.detail?.eventId) {
      // For nested encounters of selected event
      const targetEvent = options.events.find(
        (e) => e.extendedProps.eventId === event.detail.eventId,
      );
      if (targetEvent) selectedEvent = targetEvent?.extendedProps ?? {};
    } else if (event?.nestedEncounter?.length) {
      // For nested encounters of selected encounter
      const selectedEventContainsEncounterId = event.nestedEncounter[0].id;

      const targetNestedEncounter = options.events.find(
        (e) =>
          e.extendedProps.eventType == "encounter" &&
          e.extendedProps.mergedEncounters.some(
            (enc) => enc.id == selectedEventContainsEncounterId,
          ),
      );
      if (targetNestedEncounter) {
        const newNestedEncounter =
          targetNestedEncounter.extendedProps?.mergedEncounters ?? [];
        selectedEvent = {
          ...targetNestedEncounter.extendedProps,
          nestedEncounter: newNestedEncounter,
          startDate: selectedEvent.startDate,
          endDate: selectedEvent.endDate,
        };
      }
    } else {
      isEventNestedModalOpen = false;
      selectedEvent = {};
    }
    isReloadingEvent = false;
  };

  const calendarPrev = () => {
    if (ec && ec.prev) ec.prev();
    filters.jumpDate = getYYYYMMDD(ec.getOption("date"));
  };

  const calendarNext = () => {
    if (ec && ec.next) ec.next();
    filters.jumpDate = getYYYYMMDD(ec.getOption("date"));
  };

  const calendarToday = () => {
    if (ec) ec.setOption("date", new Date());
    filters.jumpDate = getYYYYMMDD(ec.getOption("date"));
  };

  const onMouseEnter = (info) => {
    info.el.style.zIndex = Number(99);
    if (
      info.view.type === "dayGridMonth" ||
      info?.event?.extendedProps?.eventType === "encounter"
    ) {
      clearTimeout(tooltipDisplayDelayTimer);
      showMonthlyTooltip = false;
      isMonthView = info.view.type === "dayGridMonth";
      setTimeout(() => {
        tooltipInfo = info;
        showMonthlyTooltip = true;
      }, 1);
    }
  };

  const onMouseLeave = (info) => {
    if (info.el.style.zIndex > Number(0)) info.el.style.zIndex = Number(1);
    if (showMonthlyTooltip) {
      hideTooltipDelayed();
    }
  };

  const hideTooltipDelayed = () => {
    tooltipDisplayDelayTimer = setTimeout(() => {
      tooltipInfo = {};
      showMonthlyTooltip = false;
    }, tooltipDisappearTimeDelayMS);
  };

  const continueTooltipDisplay = () => {
    clearTimeout(tooltipDisplayDelayTimer);
  };

  const startDuplicateEvent = () => {
    selectedDoctor = {
      id: selectedEvent.doctorId,
      key: selectedEvent.doctorId,
      name: selectedEvent.doctorName,
      value: selectedEvent.doctorName,
    };

    // eventType set as "list" because only list can show dup icon from daily planner.
    // used string value due to legacy issue
    createEventProps = {
      ...selectedEvent,
      doctorId: selectedEvent.doctorId,
      fromTime: selectedEvent.startDate[1],
      toTime: selectedEvent.endDate[1],
      eventType:
        selectedEvent.eventType === DirectCalendarEventTypes.List
          ? "list"
          : "onCall",
      isRepeating:
        (selectedEvent.eventType == 1 || selectedEvent.eventType == 2) &&
        selectedEvent.repeatIntervalType > 0,
    };
    if (selectedEvent.hospitalId && selectedEvent.hospitalName)
      selectedHospital = {
        id: selectedEvent.hospitalId,
        name: selectedEvent.hospitalName,
      };

    if (selectedEvent.surgeonId && selectedEvent.surgeonName)
      selectedSurgeon = {
        id: selectedEvent.surgeonId,
        displayLabel: selectedEvent.surgeonName,
      };

    delete createEventProps.eventId;
    delete createEventProps.id;
    delete createEventProps.parentId;

    isEventNestedModalOpen = false;
    isCreateEventModalOpen = true;
  };

  $: isSelectedToday =
    getYYYYMMDD(new Date()) == getYYYYMMDD(filters?.jumpDate);

  const goToDefaultView = () => {
    $user.settings.dailyPlannerVerticalView = false;
    dispatch("defaultView");
  };

  const clearIdleRefreshTimer = () => {
    window.clearInterval(idleRefreshTimer);
  };

  const startIdleRefreshTimer = () => {
    clearIdleRefreshTimer();
    idleRefreshTimer = setInterval(() => {
      loadCalendarEvents(false, true);
    }, CalendarIdleRefreshIntervalSeconds * 1000);
  };
</script>

<div class="flex flex-col gap-4 flex-1">
  <div class="flex flex-row justify-between items-end">
    <div
      class="flex w-auto items-end gap-4 flex-wrap items-center {$isMobile
        ? ''
        : 'justify-start'}"
    >
      {#if !isDoctorView}
        {#if isVisibleDoctorsCollapsed}
          <p
            class="cursor-pointer text-secondary hover:underline"
            on:click={() =>
              (isVisibleDoctorsCollapsed = !isVisibleDoctorsCollapsed)}
          >
            <i
              style="font-size:16px"
              class={`fa cursor-pointer rounded-lg ${
                isVisibleDoctorsCollapsed ? "rotate-180" : "rotate-back"
              }`}
            >
              &#xf104;
            </i>

            {isVisibleDoctorsCollapsed ? "View Doctors" : "Hide Doctors"}
          </p>
        {/if}
        <DefaultDoctorFilter
          bind:filter={filters}
          filterPropertyName={"doctorId"}
          inputClass="bg-transparent"
          isLong
          onChangeFunc={() => {
            const doctorIds = filters.doctorId
              ? filters.doctorId.split(",").map((n) => Number(n))
              : [];

            searchDoctorArr = doctorIds.length
              ? allDoctors.filter((doctor) => doctorIds.includes(doctor.id))
              : allDoctors;

            handleSearchDoctorsChange(searchDoctorArr);
          }}
        />

        <SearchInput
          bind:value={searchAccountText}
          title="Doctor"
          isLong
          inputClass="borderB"
        />
      {/if}

      <SearchInput
        bind:value={filters.jumpDate}
        title="Date"
        type="date"
        isLong
        inputClass="borderB z-[1000] bg-transparent"
        inputFunc={handleSelectJumpDate}
        isDisable={isLoading}
        showTimeInputClearButton={false}
        showScrollYearOptions
      />

      <SearchSelect
        title="Billing Status"
        inputClass="bg-transparent"
        isLong
        bind:value={filters.billingStatus}
        onChangeFunc={handleFilterSelect}
        isDisabled={isLoading}
      >
        <option value="0,1,2,3,4" selected>All Statuses exclude complete</option
        >
        <option value="0,1,2,3,4,5">All Statuses</option>
        {#each BillingStatus as billingStatus}
          <option value={billingStatus.id}>{billingStatus.type}</option>
        {/each}
      </SearchSelect>

      <SurgeonSelector
        inputClass="bg-transparent"
        isDisabled={isSearchingSurgeon}
        bind:selectedSurgeonIds={selectedAccountDoctors}
        onChangeFunc={handleSelectSurgeonFromSearch}
        showCollapsedPlaceholder
        showSelected
        zIndex="3"
      />

      <SearchSelect
        title="Event Type"
        isLong
        bind:value={filters.eventType}
        onChangeFunc={handleFilterSelect}
        isDisabled={isLoading}
        inputClass="bg-transparent"
      >
        <option value={undefined} selected>All Types</option>
        {#each accountCalendarEventType as aceType}
          {#if aceType.doctorSpecific}
            <option value={aceType.id}>{aceType.type}</option>
          {/if}
        {/each}
        <option value="encounter">Patient Encounter</option>
      </SearchSelect>

      <div class="flex flex-col">
        <p class="text-[11px] font-[500]">Event Category</p>
        <CalendarEventCategorySelector
          bind:value={filters.eventCategory}
          on:change={handleFilterSelect}
          disabled={isLoading}
          emptyOptionName="All Categories"
          borderClass="border-bottom"
          widthClass="w-48"
        />
      </div>

      <div class="flex flex-col">
        <p class="text-[11px] font-[500]">Show Encounters</p>
        <input
          type="checkbox"
          class="toggle toggle-secondary"
          bind:checked={filters.showEncountersOnDailyPlanner}
          on:change={handleFilterSelect}
        />
      </div>

      <div class="flex flex-col">
        <p class="text-[11px] font-[500]">Vertical View</p>
        <input
          type="checkbox"
          class="toggle toggle-secondary"
          checked={true}
          on:change={goToDefaultView}
        />
      </div>

      <div class="flex-1" />

      <ResetFilterLink
        bind:filter={filters}
        defaultFilter={{ billingStatus: "0,1,2,3,4" }}
        specialVariables={{
          selectedAccountDoctors,
        }}
        onResetFilter={() => {
          selectedAccountDoctors = [];
          ec.setOption("date", new Date(filters.jumpDate));
          // setOption Date will trigger calendar reload;
          updateURL();
        }}
      />

      <div class="flex w-fit border rounded rounded-lg mx-2 right-0">
        <button
          class="btn btn-ghost btn-outline btn-md text-[16px] font-normal normal-case rounded-r-none"
          on:click|preventDefault={calendarPrev}
          disabled={isLoading}
        >
          Prev Day
        </button>
        <div class="divider divider-horizontal p-0" />
        <button
          class={`btn btn-ghost btn-outline btn-md text-[16px] font-normal normal-case rounded-none ${
            isSelectedToday ? "bg-gray-200 font-medium" : ""
          }`}
          on:click|preventDefault={calendarToday}
          disabled={isLoading}
        >
          {getDisplayDate(filters?.jumpDate)}
        </button>
        <div class="divider divider-horizontal p-0" />
        <button
          class="btn btn-ghost btn-outline btn-md text-[16px] font-normal normal-case rounded-l-none"
          on:click|preventDefault={calendarNext}
          disabled={isLoading}
        >
          Next Day
        </button>
      </div>
    </div>
  </div>

  <div class="relative flex-1">
    <div class="absolute inset-0 flex flex-1 overflow-auto gap-x-4">
      {#if !isDoctorView && !isVisibleDoctorsCollapsed}
        <CalendarDoctorListSidebar
          bind:searchDoctorArr
          bind:allDoctors
          bind:isVisibleDoctorsCollapsed
          bind:isLoadingDoctors
          {handleSearchDoctorsChange}
        />
      {/if}
      <div class="flex flex-1 flex-col gap-y-3 rounded-xl bg-white p-4">
        <div class="flex-1 relative">
          <div
            id="calendarContainer"
            style="z-index: 2"
            class="flex flex-1 inset-0 absolute overflow-auto "
          >
            <Calendar
              bind:this={ec}
              {plugins}
              {options}
              style="--DefaultColumnWidthPx: {DefaultColumnWidthPx}px"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <DailyCalendarExpandSidePanel
    encounterProps={selectedEncounterProps}
    {closeExpandSidePanel}
  />
</div>

<NotificationModal
  isModalOpen={isEventTimeChangeModalOpen}
  title="Edit Event"
  closeModal={() => closeEventTimeChangeModal(true)}
  confirmAction={updateEventTime}
  isConfirming={isUpdatingEventTime}
  closeModalFunction={() => closeEventTimeChangeModal(true)}
>
  <div class="flex flex-col gap-y-4">
    <p>
      Change this {selectedEventOldDetails.oldEventType} date/time to:
    </p>

    {#if !selectedEventNewDetails.mergedEncounters}
      <p>
        {getDateFromDateString(
          selectedEventNewDetails.start,
          undefined,
          undefined,
          true,
        )} - {getDateFromDateString(
          selectedEventNewDetails.end,
          undefined,
          undefined,
          true,
        )}?
      </p>
    {:else}
      {#each selectedEventNewDetails.mergedEncounters as encounter}
        <p>
          Encounter {encounter.id}: {getDateFromDateString(
            encounter.scheduledDate,
          )} - {getDateFromDateString(encounter.dischargeDate)}?
        </p>
      {/each}
    {/if}
  </div>
</NotificationModal>

<NotificationModal
  isModalOpen={isCreateEventModalOpen}
  title="Create Event"
  closeModal={closeCreateEventModal}
  confirmAction={confirmCreateEvent}
  isConfirming={isCreatingEvent}
  modalMaxWidth="max-w-[640px]"
  closeModalFunction={closeCreateEventModal}
  noContentPadding
>
  <div class="flex flex-col flex-1 gap-y-4 overflow-y-auto p-6">
    <div class="flex flex-1 gap-x-4 items-center">
      <p class="w-32">Doctor:</p>
      <p>{selectedDoctor?.name}</p>
    </div>

    <div class="flex flex-row flex-1 gap-x-4 items-center">
      <p class="w-32">From:</p>
      <div class="flex flex-1 gap-x-2 items-center w-[268px]">
        <DateInput
          bind:value={createEventProps.fromDate}
          wrapperClass="input input-sm px-0 input-bordered flex-1"
          wrapperClassFocus="inputFocus"
        />

        {#if createEventProps.isFullDay}
          - 00:00
        {:else}
          <input
            type="time"
            bind:value={createEventProps.fromTime}
            class="input input-sm input-bordered"
          />
        {/if}
      </div>
    </div>

    <div class="flex flex-row flex-1 gap-x-4 items-center">
      <p class="w-32">To:</p>
      <div class="flex flex-1 gap-x-2 items-center w-[268px]">
        <DateInput
          bind:value={createEventProps.toDate}
          wrapperClass="input input-sm px-0 input-bordered flex-1"
          wrapperClassFocus="inputFocus"
        />

        {#if createEventProps.isFullDay}
          - 23:59
        {:else}
          <input
            type="time"
            bind:value={createEventProps.toTime}
            class="input input-sm input-bordered"
          />
        {/if}
      </div>
    </div>

    <div class="flex flex-1 gap-x-4 items-center">
      <p class="w-32">Event Type:</p>

      <select
        class="select select-sm select-bordered flex-1"
        bind:value={createEventProps.eventType}
      >
        <option value="encounter">Create Patient Encounter</option>
        <option value="leave">Set Leave</option>
        <option value="list">List</option>
        <option value="onCall">On-Call</option>
      </select>
    </div>

    {#if createEventProps.eventType !== "encounter"}
      <div class="flex flex-1 gap-x-4 items-center">
        <p class="w-32">Event Category:</p>

        <CalendarEventCategorySelector
          bind:value={createEventProps.eventCategory}
          widthClass="flex-1"
        />
      </div>
    {/if}

    {#if createEventProps.eventType == "list" || createEventProps.eventType == "onCall"}
      <div class="flex flex-1 gap-x-4 items-center">
        <p class="w-32">Repeating:</p>

        <div class="flex flex-1 items-center justify-between">
          <input
            type="checkbox"
            class="toggle toggle-secondary"
            bind:checked={createEventProps.isRepeating}
            on:change={() => handleToggleRecurrenceModalOpen(createEventProps)}
          />

          {#if createEventProps.isRepeating}
            <div
              class="flex items-center gap-x-2 cursor-pointer"
              on:click={() => handleToggleRecurrenceModalOpen(createEventProps)}
            >
              <i class={editIcon} />
              <p>Edit</p>
            </div>
          {/if}
        </div>
      </div>
    {/if}

    {#if createEventProps.eventType == "list"}
      <div class="flex flex-row flex-1 gap-x-4 items-center">
        <p class="w-32">Hospital:</p>
        <!-- width set to prevent long options go too wide -->
        <div class="flex flex-1 w-96">
          <Select
            bind:value={selectedHospital}
            class="flex flex-1 max-h-[35px] rounded-full"
            loadOptions={loadHospitals}
            items={hospitalOptions}
            {groupBy}
            itemId="id"
            label="name"
            noOptionsMessage="No hospital found"
            placeholder="Hospital"
            disabled={!isDoctorView && !selectedDoctor}
            loading={isLoadingSuggestedHospitals}
            on:focus={loadSuggestedHospitals}
          />
        </div>
      </div>

      <div class="flex flex-row flex-1 gap-x-4 items-center">
        <p class="w-32">Surgeon:</p>
        <!-- width set to prevent long options go too wide -->
        <div class="flex flex-1 w-96">
          <Select
            bind:value={selectedSurgeon}
            class="flex flex-1 max-h-[35px] rounded-full"
            items={surgeonOptions}
            itemId="id"
            label="displayLabel"
            {groupBy}
            noOptionsMessage="No surgeon/referrer found"
            placeholder="Surgeon/Referrer"
            disabled={!isDoctorView && !selectedDoctor}
            loading={isLoadingSuggestedAccountDoctors}
            on:focus={loadSuggestedAccountDoctor}
          />
        </div>
      </div>

      <div class="flex flex-1 w-full">
        <p
          class="underline cursor-pointer text-secondary"
          on:click={openCreateNewSurgeonModal}
        >
          Add new surgeon/referrer
        </p>
      </div>
    {/if}

    {#if createEventProps.eventType !== "encounter"}
      <div class="flex flex-1 gap-x-4">
        <p class="w-32">Description:</p>
        <div class="flex flex-1 w-full">
          <textarea
            class="textarea textarea-bordered w-full"
            rows="6"
            autocomplete="off"
            bind:value={createEventProps.description}
          />
        </div>
      </div>
    {/if}
  </div>
</NotificationModal>

{#if isEventNestedModalOpen}
  <CalendarEventsNestedEncounterModal
    isModalOpen={isEventNestedModalOpen}
    {selectedEvent}
    isLoading={isReloadingEvent}
    closeModal={closeNestedEncounterModal}
    {handleEditNestedEncounter}
    {handleEditEventDetails}
    {reloadReOpenCurrentEventModal}
    on:reload={loadCalendarEvents}
    on:duplicateEvent={startDuplicateEvent}
  />
{/if}

{#if isCalendarRecurrenceModalOpen}
  <CalendarRecurrenceModal
    bind:isModalOpen={isCalendarRecurrenceModalOpen}
    bind:eventObj={createEventProps}
    startDate={createEventProps.fromDate}
  />
{/if}

<ModifyAccountEventModal
  id={selectedEvent.id}
  newValues={eventNewValues}
  isModalOpen={isModifyEventModalOpen}
  nestedEncounter={selectedEvent.nestedEncounter}
  closeModal={closeEditEventModal}
  isOpenFromCalendar
  afterSubmit={async () => {
    if (accountId || searchDoctorArr.length) {
      await handleSearchDoctorsChange(
        accountId ? [accountId] : searchDoctorArr,
      );
    }
  }}
/>

{#if isCreateNewSurgeonModalOpen}
  <CreateSurgeonModal
    {isCreateNewSurgeonModalOpen}
    reloadSurgeonDetails={() => loadAccountDoctors(selectedDoctorID)}
    closeFunc={closeCreateNewSurgeonModal}
    bind:selectedSurgeon
    doctorId={selectedDoctorID}
    existedOptions={surgeonOptions}
  />
{/if}

<CalendarTooltip
  bind:showMonthlyTooltip
  {tooltipInfo}
  {isMonthView}
  {isDoctorCalendar}
  on:mouseEnter={continueTooltipDisplay}
  on:mouseLeave={hideTooltipDelayed}
/>

<style>
  .rotate-back {
    transform: rotate(0deg);
    transition: 0.2s linear;
  }

  .rotate-180 {
    transform: rotate(180deg);
    transition: 0.2s linear;
  }

  .divider,
  .divider-horizontal {
    padding: 0;
    margin: 0;
    width: 0;
  }

  /* :global(.ec-day) {
    min-width: var(--DefaultColumnWidthPx);
  } */
</style>
